import { ApplicationElement, html } from '../application_element/element.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { IntersectionController } from '@lit-labs/observers/intersection-controller.js';
import translations from './translations.js';
import style from './style.lit.css';

import ChatMessageBase from '../chat_message_base/element.js';

import '../chat_message_reply/element.js';

import { LongTouchController } from '../../lib/lit/long_touch_controller.js';

import { ChatMessageEvent } from '../chat_message_base/lib/chat_message_event.js';

/**
 * Message component
 *
 * @prop userName - Sender full name
 * @prop userPicture - URL of a picture
 * @prop createdAt - Message created timestamp
 * @prop my - Current user is message author
 * @prop isRepresentative - Is user official
 * @prop errors - Error description
 * @prop attachment - Attachment information ({ url, original_filename, type, download_url })
 * @prop images - Image sources ({ image_small, image_medium, image_large })
 * @prop uploading - When attachment / image is uploading
 */
export class ChatMessageNew extends ChatMessageBase {
  /** @internal */
  static translations = translations;

  static styles = [ApplicationElement.styles, style];

  // Intersection logic is only for chat list
  visibilityIntersector = new IntersectionController(this, {
    config: { threshold: 0.5 },
    callback: ([entry]) => this.onIntersect(entry.isIntersecting),
  });

  longTouchController = new LongTouchController(this, {
    callback: () => this.menu.openMenu(),
  });

  willUpdate(changedProperties) {
    super.willUpdate(changedProperties);

    if (changedProperties.has('editMode') && this.editMode) {
      if (!this.editOn) {
        return this.dispatchEvent(new ChatMessageEvent('exit-edit-mode', this));
      }

      this.scrollIntoView();
    }
  }

  onIntersect(visible) {
    if (visible) {
      this.dispatchEvent(new ChatMessageEvent('seen', this));
    }
  }

  render() {
    return html`
      ${this.my ? null : this.renderAvatar()}
      <div class=${classMap(this.classes)}>
        ${this.menuOn ? this.renderMenu() : null}
        ${this.userName && this.renderName()}
        ${this.hasParentMessage && !this.destroyed
          ? this.renderOriginalMessage()
          : null}
        ${this.errors && this.renderMessageError()}
        ${this.attachmentOn ? this.renderAttachment() : null}
        ${this.bodyOn ? this.renderMessageBody() : null}
        <div class="info" tabindex=${ifDefined(this.my && '0')}>
          ${this.my ? this.renderTooltip() : null} ${this.renderCreatedAt()}
          ${this.my ? this.renderStatusIndicator() : null}
        </div>
        ${(this.uploading && this.renderProgress()) || null}
        ${this.unsent ? this.renderUnsentActions() : null}
      </div>
    `;
  }
}

ChatMessageNew.registerTagName('ef-chat-message-new');

customElements.get('ef-chat-message-new') ||
  customElements.define('ef-chat-message-new', ChatMessageNew);

export default ChatMessageNew;
